'use client';

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import type { ConversationTreeNode } from '../../lib/conversationTree';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/shadcn/collapsible';
import { Item, ItemGroup } from '../../components/ui/shadcn/item';
import { ConversationListRow } from './ConversationListRow';
import type { ConversationListProps } from './conversationListModel';

/** Presentation only: the controller supplies page fragments, totals and authorized row capabilities. */
export function ConversationList(props: ConversationListProps) {
  const { t } = useTranslation();
  const uid = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [pressed, setPressed] = useState<Pick<ConversationListProps, 'sections' | 'items'> | null>(null);
  // Section keys scope row identity: a live unread/read update otherwise unmounts the control between
  // pointerdown and click. Keep its layout until dispatch finishes, not merely until pointerup.
  useEffect(() => {
    const release = () => setPressed(null);
    const outside = (event: Event) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) release();
    };
    document.addEventListener('click', release);
    document.addEventListener('pointerup', outside, true);
    document.addEventListener('pointercancel', release, true);
    document.addEventListener('pointerdown', release, true);
    document.addEventListener('focusin', outside, true);
    window.addEventListener('blur', release);
    return () => {
      document.removeEventListener('click', release);
      document.removeEventListener('pointerup', outside, true);
      document.removeEventListener('pointercancel', release, true);
      document.removeEventListener('pointerdown', release, true);
      document.removeEventListener('focusin', outside, true);
      window.removeEventListener('blur', release);
    };
  }, []);
  const layout = pressed ?? props;
  const focusedControl = useRef<{ id: string; control: string; node: HTMLButtonElement } | null>(null);
  useLayoutEffect(() => {
    const previous = focusedControl.current;
    // Moving a pinned root into another section remounts its controls. Focus lost with them falls to the body,
    // or to an enclosing dialog when its focus trap catches it, until the row lands on the shown page; take
    // that back, but do not steal live focus from any other control.
    if (!previous || previous.node.isConnected || !document.activeElement?.contains(rootRef.current)) return;
    const row = Array.from(rootRef.current!.querySelectorAll<HTMLElement>('[data-conversation-id]'))
      .find(candidate => candidate.dataset.conversationId === previous.id);
    const control = Array.from(row?.querySelectorAll<HTMLElement>('[data-conversation-control]') ?? [])
      .find(candidate => candidate.dataset.conversationControl === previous.control);
    const button = control instanceof HTMLButtonElement ? control : control?.querySelector('button');
    button?.focus({ preventScroll: true });
  });
  const renderNode = (node: ConversationTreeNode) => {
    const item = layout.items.get(node.row.id);
    if (!item) throw new Error(`Missing conversation list item: ${node.row.id}`);
    const open = props.expanded.has(item.id);
    const hasBranch = node.children.length > 0 || node.jobs.length > 0
      || (props.view === 'mine' && props.subagentIds?.has(item.id) === true);
    const branchId = `${uid}-branch-${encodeURIComponent(item.id)}`;
    const title = item.title || t.brainChat.untitled;
    const selectable = props.selectionEnabled !== false && node.depth === 0 && (item.capabilities.delete || item.capabilities.export) && !props.contextIds.has(item.id) && !props.heldIds?.has(item.id);
    return <Item key={item.id} className="block border-0 p-0">
      <ConversationListRow item={item} depth={node.depth} active={props.activeId === item.id}
        selected={props.selected.has(item.id)} selectionMode={props.selectionMode} selectable={selectable}
        busy={props.busy} contextOnly={props.contextIds.has(item.id)} deleteHeld={props.heldIds?.has(item.id)} expanded={open} hasBranch={hasBranch}
        branchId={open && hasBranch ? branchId : undefined} snippet={props.snippets.get(item.id)}
        search={props.search} now={props.now} view={props.view} actions={props.actions}
        runningAgents={props.runningAgents?.get(item.id)} scheduledCount={props.scheduledCounts?.get(item.id)} scheduledLabel={props.scheduledLabel}
        hasSubagents={props.subagentIds?.has(item.id) === true}
        onToggle={() => props.onToggle(item.id)} onSelect={() => props.onSelect(item.id)}
        onRenameChange={editing => props.onRenameChange?.(item.id, editing)} />
      {open && hasBranch ? <div role="region" id={branchId} aria-label={`${title}: ${t.chat.historyTitle}`}
        className="ml-4 mt-2 min-w-0 border-l border-border pl-2">
        {node.children.length ? <ItemGroup>{node.children.map(renderNode)}</ItemGroup> : null}
        {props.renderBranch(node, item)}
      </div> : null}
    </Item>;
  };
  return <div ref={rootRef} className="min-w-0 space-y-4" aria-busy={props.busy || undefined}
    onPointerDownCapture={event => {
      if (event.button === 0 && event.target instanceof Element
        && event.currentTarget.contains(event.target) && event.target.closest('[data-conversation-id]')) {
        setPressed({ sections: layout.sections, items: layout.items });
      }
    }}
    onContextMenuCapture={() => setPressed(null)}
    onFocusCapture={event => {
      if (!(event.target instanceof HTMLButtonElement)) return;
      const row = event.target.closest<HTMLElement>('[data-conversation-id]');
      const control = event.target.closest<HTMLElement>('[data-conversation-control]');
      if (row && control) focusedControl.current = { id: row.dataset.conversationId!, control: control.dataset.conversationControl!, node: event.target };
    }}>
    {layout.sections.filter(section => section.roots.length > 0).map(section => {
      const headingId = `${uid}-section-${section.key}`;
      const label = <>{t.conversationList.groups[section.key]} <span className="tabular-nums">{section.total}</span></>;
      const list = <ItemGroup aria-labelledby={headingId}>{section.roots.map(renderNode)}</ItemGroup>;
      if (section.key !== 'pinned') return <section key={section.key} aria-labelledby={headingId} className="min-w-0">
        <h3 id={headingId} className="mb-2 flex items-center gap-2 px-3 text-small text-muted-foreground">{label}</h3>
        {list}
      </section>;
      // Only the pinned section folds: it is the one the reader chose to keep on top, so it is also the one
      // they may want out of the way. The chevron trails the count so every heading starts on one x.
      return <Collapsible key={section.key} asChild open={props.pinnedOpen} onOpenChange={props.onPinnedOpenChange}>
        <section aria-labelledby={headingId} className="min-w-0">
          <h3 id={headingId} className="mb-2 flex px-3 text-small text-muted-foreground">
            <CollapsibleTrigger className="group/fold flex items-center gap-2 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 pointer-coarse:min-h-[var(--touch-target)]">
              {label}
              <ChevronRight size={14} aria-hidden className="shrink-0 transition-transform duration-[var(--motion-base)] group-data-[state=open]/fold:rotate-90" />
            </CollapsibleTrigger>
          </h3>
          <CollapsibleContent>{list}</CollapsibleContent>
        </section>
      </Collapsible>;
    })}
  </div>;
}
